import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';
import { DwControlBaseComponent } from '@devwareapps/devware-cap';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { DocumentFolderEntity, DocumentGroupEntity } from '../../../../meta-data/app-meta-data.service';

@Component({
  selector: 'app-folder-selector',
  templateUrl: './folder-selector.component.html',
  styleUrls: ['./folder-selector.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FolderSelectorComponent),
    multi: true,
  }]
})
export class FolderSelectorComponent extends DwControlBaseComponent implements OnChanges {
  @Input() documentGroupId: number;

  @Output() folderChanged = new EventEmitter<DocumentFolderEntity>();

  @Input() hideFolderId : number;

  @Input() hideLabel: boolean = false;

  @Input() rootName: string = 'Main Folder';

  documentGroup: DocumentGroupEntity;

  folders: TreeNode[] = [];

  rootNode : TreeNode;

  constructor(private documentRepositoryService: DocumentsRepositoryService) {
    super();

    
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadDocumentGroup();
  }

  loadDocumentGroup(): void {
    if (!this.documentGroupId) {
      return;
    }

    this.documentRepositoryService.getDocumentGroup(this.documentGroupId).subscribe(documentGroup => {
      // Handle the loaded documents group as needed
      this.documentGroup = documentGroup;

      this.buildFolderTree();

      // Call this again so the correct tree node is selected
      this.translateTo(this.value);
    });
  }

  private buildFolderTree() {
    this.rootName = this.documentRepositoryService.getRootFolderName();

    this.rootNode = {
      label: this.rootName,
      data: null,
      children: this.buildTreeNodes(),
      expanded: true
    };

    this.folders = [this.rootNode];
  }

  private buildTreeNodes(currentFolderId?: number): TreeNode[] {
    const nodes: TreeNode[] = [];

    const currentFolders = this.documentGroup.DocumentFolder.filter(folder => (!currentFolderId && !folder.ParentDocumentFolderId) || folder.ParentDocumentFolderId === currentFolderId);

    for (const folder of currentFolders) {
      const node = this.buildFolderNode(folder);

      if (node) {
        nodes.push(node);
      }
    }

    return nodes;
  }

  translateTo(value: any) {
    if (!this.folders) {
      return;
    }

    if (!value) {

      this.folderChanged.emit(null);
      return this.rootNode;
    }

    const foundNode = this.findFolderNode(value, this.folders);

    this.folderChanged.emit(null);

    this.expandParentNodes(value, this.folders);

    return foundNode;
  }

  translateFrom(translatedValue: TreeNode) {
    if (!translatedValue) {
      return null;
    }

    const folderId = translatedValue.data?.DocumentFolderId || null;

    this.folderChanged.emit(translatedValue.data);

    return folderId;
  }

  private buildFolderNode(folder: DocumentFolderEntity, currentFolderId?: number): TreeNode {
    const treeNode: TreeNode = {
      label: folder.FolderName,
      data: folder,
      children: this.buildTreeNodes(folder.DocumentFolderId)
    };

    if (folder.DocumentFolderId === this.hideFolderId) {
      return null;
    }

    if (folder.DocumentFolderId === this.translatedValue) {
      treeNode.expanded = true;
    }

    return treeNode;
  }

  private findFolderNode(folderId: number, nodes: TreeNode[]): TreeNode {
    for (const node of nodes) {
      if (node.data?.DocumentFolderId === folderId) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const foundNode = this.findFolderNode(folderId, node.children);
        if (foundNode) {
          return foundNode;
        }
      }
    }

    return null;
  }

  private expandParentNodes(folderId: number, nodes: TreeNode[]): boolean {
    for (const node of nodes) {
      if (node.data === folderId) {
        return true;
      } 
      if (node.children && node.children.length > 0) {
        const foundInChildren = this.expandParentNodes(folderId, node.children);
        if (foundInChildren) {
          node.expanded = true;
          return true;
        }
      }
    }
    return false;
  }

}
